import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, X } from "lucide-react";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { cn } from "@/lib/utils";
import { useMediaQuery } from "@/hooks/use-media-query";
import { num, longDate } from "@/lib/sk";
import { PriceText, StatusPill } from "@/components/produkter/bits";
import { KIND_LABEL, VARIANT_LABEL, VARIANT_ORDER, familiesOf, unitLabel } from "@/lib/produkter";

function Fact({ label, value, testId }) {
  return (
    <div className="min-w-0 py-2.5" data-testid={testId}>
      <div className="text-[11.5px] text-muted-foreground">{label}</div>
      <div className="mt-0.5 text-[14px] text-foreground">{value}</div>
    </div>
  );
}

function Block({ title, children, testId }) {
  return (
    <section className="mt-7" data-testid={testId}>
      <h3 className="mb-2 text-[12px] font-semibold uppercase tracking-[0.08em] text-muted-foreground/80">{title}</h3>
      {children}
    </section>
  );
}

/** Everything about one price-list line or rate: price, how it bills, where it comes from, the tariffs around it, where it is used. */
export function LineSheet({ line, catalog, onClose, onOpen, onGoArea }) {
  const mobile = !useMediaQuery("(min-width: 768px)");
  if (!line) return null;
  const prov = catalog.pricelist?.provenance || {};
  const fam = line.family ? familiesOf(catalog.lines.filter((l) => l.family === line.family))[0] : null;
  const area = catalog.areas.find((a) => a.key === line.area);
  const rate = catalog.rates.find((r) => r.pricelist_line_id === line.id);
  const refs = line.usage?.refs || [];
  return (
    <Sheet open onOpenChange={(o) => { if (!o) onClose(); }}>
      <SheetContent side={mobile ? "bottom" : "right"} hideClose aria-describedby={undefined} className={cn("flex flex-col gap-0 p-0", mobile ? "h-[92dvh] rounded-t-[20px]" : "w-[min(92vw,480px)] sm:max-w-[min(92vw,480px)]")} data-testid="produkt-sheet">
        <SheetTitle className="sr-only">{line.description}</SheetTitle>
        <div className="flex items-start gap-3 border-b border-black/[0.06] px-6 pb-4 pt-5">
          <div className="min-w-0 flex-1">
            <div className="text-[12px] text-muted-foreground" data-testid="produkt-sheet-group">{line.group_label}{line.variant && line.variant !== "normal" ? ` · ${VARIANT_LABEL[line.variant]}` : ""}</div>
            <h2 className="mt-0.5 text-[18px] font-semibold leading-snug tracking-[-0.01em] text-[hsl(var(--brand-strong))]" data-testid="produkt-sheet-title">{line.description}</h2>
          </div>
          <button type="button" onClick={onClose} className="flex h-8 w-8 shrink-0 items-center justify-center rounded-[8px] text-muted-foreground hover:bg-black/[0.05] hover:text-foreground" aria-label="Lukk" data-testid="produkt-sheet-close"><X className="h-4 w-4" /></button>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto px-6 pb-8 scrollbar-thin">
          <div className="mt-5 flex items-end justify-between gap-4">
            <PriceText line={line} size="lg" testId="produkt-sheet-price" />
            <StatusPill status={prov.status || "bekreftet"} />
          </div>
          {line.price_kind === "note" && line.note ? <p className="mt-2 text-[13.5px] text-foreground/80" data-testid="produkt-sheet-note">{line.note}</p> : null}
          <div className="mt-4 grid grid-cols-2 gap-x-6 divide-y-0 border-t border-black/[0.06] sm:grid-cols-3">
            <Fact label="Fakturering" value={KIND_LABEL[line.price_kind] || "–"} testId="produkt-fact-billing" />
            <Fact label="Enhet" value={line.unit ? unitLabel(line.unit) : "–"} testId="produkt-fact-unit" />
            <Fact label="Minste mengde" value={line.min_qty ? `${num(line.min_qty)} ${unitLabel(line.min_unit || line.unit)}` : "ingen"} testId="produkt-fact-min" />
          </div>

          <Block title="Kost og dekning" testId="produkt-sheet-cost">
            {rate && rate.cost != null ? (
              <p className="text-[13.5px] text-foreground/85">Kost {num(rate.cost)} → salg {num(rate.price)} pr. time · DB {num(rate.db_pct, 1)} % <span className="text-muted-foreground">({rate.status}, {rate.source})</span></p>
            ) : (
              <p className="text-[13.5px] text-foreground/85">Intern kost er <span className="font-medium">ukjent</span> for denne linjen. Den settes i kalkylen – og vises der som «simulert» til den er bekreftet. Aldri som 0.</p>
            )}
          </Block>

          <Block title="Kilde" testId="produkt-sheet-source">
            <p className="text-[13.5px] text-foreground/85">{prov.source || catalog.pricelist?.source}{catalog.pricelist?.valid_from ? ` · gyldig fra ${longDate(catalog.pricelist.valid_from)}` : ""}</p>
            {prov.note ? <p className="mt-1 text-[12.5px] text-muted-foreground">{prov.note}</p> : null}
            <p className="mt-1 font-mono text-[11.5px] text-muted-foreground/70">{line.id} · satsversjon v{catalog.version?.version}</p>
          </Block>

          {fam && fam.lines.length > 1 ? (
            <Block title="Satser i samme tjeneste" testId="produkt-sheet-variants">
              <div className="divide-y divide-black/[0.05] rounded-[10px] border border-black/[0.06]">
                {VARIANT_ORDER.filter((v) => fam.variants[v]).map((v) => {
                  const l = fam.variants[v];
                  const me = l.id === line.id;
                  return <button key={v} type="button" onClick={() => onOpen(l.id)} className={cn("flex w-full items-center justify-between gap-3 px-3 py-2 text-left text-[13.5px] transition-colors hover:bg-black/[0.02]", me && "bg-[hsl(var(--brand)/0.04)] font-medium")} data-testid={`produkt-sheet-variant-${v}`}><span>{VARIANT_LABEL[v]}</span><PriceText line={l} size="sm" /></button>;
                })}
              </div>
            </Block>
          ) : null}

          <Block title="Brukt i saker" testId="produkt-sheet-usage">
            {refs.length ? (
              <div className="divide-y divide-black/[0.05] rounded-[10px] border border-black/[0.06]">
                {refs.map((r) => (
                  <Link key={r.id} to={`/p/${r.id}?tab=kalkyle`} className="flex items-center gap-3 px-3 py-2 text-[13.5px] transition-colors hover:bg-black/[0.02]" data-testid={`produkt-usage-case-${r.id}`}>
                    <span className="font-mono text-[12px] text-muted-foreground">#{r.number}</span>
                    <span className="min-w-0 flex-1 truncate">{r.title || r.customer}{r.title && r.customer ? <span className="text-muted-foreground"> · {r.customer}</span> : null}</span>
                    <span className="shrink-0 font-mono text-[11.5px] tabular-nums text-muted-foreground">{r.elements} {r.elements === 1 ? "linje" : "linjer"}</span>
                    <ArrowUpRight className="h-3.5 w-3.5 shrink-0 text-muted-foreground/60" />
                  </Link>
                ))}
              </div>
            ) : <p className="text-[13.5px] text-muted-foreground">Ikke brukt i noen kalkyle ennå.</p>}
          </Block>

          {area ? (
            <Block title="Leveranseområde" testId="produkt-sheet-area">
              <button type="button" onClick={() => onGoArea(area.key)} className="text-[13.5px] font-medium text-[hsl(var(--brand-strong))] underline-offset-[3px] hover:underline" data-testid="produkt-sheet-area-link">{area.label} →</button>
            </Block>
          ) : null}

          <p className="mt-8 text-[12.5px] text-muted-foreground">Priser endres i <Link to="/settings/satser" className="font-medium text-foreground/80 underline-offset-[3px] hover:underline" data-testid="produkt-sheet-satser-link">Kalkyle &amp; satser</Link> som ny versjon med revisjonsspor. Saker beholder sin versjon til de oppdateres.</p>
        </div>
      </SheetContent>
    </Sheet>
  );
}
